import React from 'react';
import Link from 'next/link';
import { Container } from 'components/base';
import { ScriptMarkupBreadcrumb, getCloudinaryUrl } from 'components/rich';
import useDataContext from 'data/context/data';

import Breadcrumb from '../components/breadcrumb';
import { MailStage } from '../components/holidayCards/greeting';
import { ctaTracker } from '../components/landing/shared';
import { Head, Layout } from '../components/layout';
import PdpIcon from '../components/product/pdpIcon';
import SETTINGS from '../settings';

// Photo Cards hub (docs/cards-hub.md): one choice between the holiday and the greeting card
// pages, nothing else. Title, meta, H1 and the nav label come from content/categories/cards.md.
const CATEGORY_ID = 'cards';
const track = ctaTracker('cards_hub');

// Stage pieces in design pixels, [x, y, width, rotation], desktop 588 x 420, phone 350 x 236.
// Every collection fans three current SKU renders, the middle one on top.
const fan = (left, middle, right) => [
  { kind: 'card', skuId: left, at: [44, 96, 200, 6], phone: [34, 44, 130, 6], priority: true },
  { kind: 'card', skuId: right, at: [344, 96, 200, -6], phone: [186, 18, 130, -6], priority: true },
  { kind: 'card', skuId: middle, at: [194, 64, 200, 0], phone: [110, 24, 130, 0], priority: true },
];

const COLLECTIONS = [
  {
    id: 'holiday',
    categoryId: 'custom-photo-holiday-cards',
    title: 'Holiday Photo Cards',
    body: 'Christmas, New Year and Hanukkah designs for sharing your year in photos.',
    cta: 'Explore Holiday Cards',
    alt: 'Christmas, New Year and Hanukkah photo card designs',
    pieces: fan('photo-card-44', 'photo-card-104', 'photo-card-84-foil'),
  },
  {
    id: 'greeting',
    categoryId: 'custom-photo-greeting-cards',
    title: 'Photo Greeting Cards',
    body: 'Send a thank-you, announce a new baby, save the date or say hello.',
    cta: 'Explore Greeting Cards',
    alt: 'Save the date, thank you and birth announcement photo card designs',
    pieces: fan('photo-card-139', 'photo-card-143', 'photo-card-115'),
  },
];

const CardsHubPage = () => {
  const dataContext = useDataContext();
  const category = dataContext.getCategory(CATEGORY_ID);
  const breadcrumb = [{ name: 'Home', url: '/' }, { name: category.name }];
  const liveIds = new Set(dataContext.skuProducts.map((sku) => sku.id));

  return (
    <>
      <Head
        title={category.seoTitle ?? category.name}
        description={category.seoDescription ?? category.shortDescription}
        image={getCloudinaryUrl({ imageKey: 'categories/cards' })}
      />
      <ScriptMarkupBreadcrumb items={breadcrumb} host={SETTINGS.HOST} />

      <Layout>
        <div className="lnd ch">
          <Container className="ch-hero">
            <Breadcrumb items={breadcrumb} />
            <div className="ch-hero-row">
              <h1 className="ch-h1">{category.heroTitle ?? category.name}</h1>
              <p className="ch-intro">
                Find a photo card for what you want to say. Explore holiday designs and cards for the
                moments you celebrate all year.
              </p>
            </div>
          </Container>

          <Container className="ch-collections">
            {COLLECTIONS.map((collection) => {
              const { url } = dataContext.getCategory(collection.categoryId);
              const titleId = `ch-${collection.id}-title`;
              return (
                <Link
                  key={collection.id}
                  href={url}
                  className={`ch-tile ch-tile--${collection.id}`}
                  aria-labelledby={titleId}
                  onClick={track('explore', 'collections', collection.id)}
                >
                  <div className="ch-stage" role="img" aria-label={collection.alt}>
                    <MailStage
                      className="ch-mail"
                      size={[588, 420]}
                      phoneSize={[350, 236]}
                      pieces={collection.pieces.filter((piece) => liveIds.has(piece.skuId))}
                    />
                  </div>
                  <div className="ch-body">
                    <h2 id={titleId} className="ch-h2">
                      {collection.title}
                    </h2>
                    <p className="ch-copy">{collection.body}</p>
                    <span className="lnd-btn ch-cta">
                      {collection.cta}
                      <PdpIcon name="arrowRight" size={18} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </Container>
        </div>
      </Layout>
    </>
  );
};

export default CardsHubPage;
